<script setup lang="ts">
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iMenu,
  iMenuItem,
  iKeyIcon,
  iKeyIconShape,
  iKeyIconType,
  iKeyIconStyle,
  iKeyIconSize,
} from '@inscribe/signal';
import { registerChatAgentHookClickEvent } from '@/events-logger';

const TIME_PERIOD_OPTIONS = [
  { label: 'Past Day', value: 'past day' },
  { label: 'Past Week', value: 'past week' },
];

const store = useStore();

// Data
const isMenuOpen = ref(false);
const selectedPeriod = ref(TIME_PERIOD_OPTIONS[0]);
const timePeriodOptions = TIME_PERIOD_OPTIONS;

// Computed
const isChatAgentEnabled = computed(() => store.getters.isChatAgentEnabled);
const canReadAllOrgData = computed(() => store.getters.canReadAllOrgData);
const currentConversationUuid = computed(
  () => store.getters.currentConversationUuid,
);
const getIsGeneratingForConversationUuid = computed(
  () => store.getters.getIsGeneratingForConversationUuid,
);
const analysisPrompt = computed(
  () =>
    `Analyze fraud patterns across this workspace from the ${selectedPeriod.value.value}.`,
);
const chatIsGenerating = computed(() =>
  getIsGeneratingForConversationUuid.value(currentConversationUuid.value),
);
const analyzeButtonLabel = computed(() =>
  chatIsGenerating.value ? 'Generating...' : 'Analyze',
);

// Methods
function onTimePeriodSelect(option: { label: string; value: string }) {
  selectedPeriod.value = option;
}

function onAnalyzeButtonClick() {
  if (chatIsGenerating.value) {
    store.dispatch('openChat');
  } else {
    triggerAnalysis();
  }
}

function triggerAnalysis() {
  store.dispatch('createNewConversationUuid');
  store.dispatch('openChat');
  store.dispatch('submitConversationMessage', {
    conversationUuid: currentConversationUuid.value,
    userMessageContent: analysisPrompt.value,
    contextCustomerUuids: [],
    contextDocumentIds: [],
  });
  registerChatAgentHookClickEvent('workspace-fraud-pattern-analysis');
}
</script>

<template>
  <div
    v-if="isChatAgentEnabled && canReadAllOrgData"
    class="workspace-fraud-pattern-analysis d-flex align-items-center"
  >
    <div
      class="analysis-card d-flex align-items-center i-gap-8 i-p-6 i-radius-3xl"
    >
      <i-key-icon
        icon="medium-graph-3"
        :shape="iKeyIconShape.SQUARE"
        :type="iKeyIconType.NEUTRAL"
        :size="iKeyIconSize.LARGE"
        :icon-style="iKeyIconStyle.OUTLINE"
      />
      <div class="text-content">
        <div class="title i-text-button-2">Fraud Pattern Analysis</div>
        <div class="subtitle i-text-body-2">
          Analyze fraud trends and anomalies across your workspace
        </div>
      </div>
      <div class="actions d-flex align-items-center i-gap-4">
        <i-menu
          :close-on-item-click="true"
          :width="140"
          @i-menu-open-changed="(value: boolean) => (isMenuOpen = value)"
        >
          <template #trigger>
            <i-button
              :label="selectedPeriod.label"
              :type="iButtonTypes.NEUTRAL"
              :button-style="iButtonStyles.OUTLINE"
              :right-icon="
                isMenuOpen ? 'small_chevron_up' : 'small_chevron_down'
              "
              data-testid="fraud-pattern-analysis-period-dropdown"
              data-dd-action-name="workspace-fraud-pattern-analysis-period-dropdown"
            />
          </template>
          <template #items>
            <i-menu-item
              v-for="option in timePeriodOptions"
              :key="option.value"
              :label="option.label"
              @item-click="onTimePeriodSelect(option)"
              data-dd-action-name="workspace-fraud-pattern-analysis-period-dropdown-item"
            />
          </template>
        </i-menu>
        <i-button
          :label="analyzeButtonLabel"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.SOLID_LIGHT"
          data-testid="fraud-pattern-analysis-button"
          data-dd-action-name="workspace-fraud-pattern-analysis-generate-button"
          @button-click="onAnalyzeButtonClick"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.workspace-fraud-pattern-analysis {
  .analysis-card {
    background: var(--base);
    border: 1px solid var(--border-neutral-subtle);
    flex: 1;

    .text-content {
      flex: 1;
    }

    .title {
      color: var(--text-neutral-bold);
    }

    .subtitle {
      color: var(--text-neutral);
    }
  }
}
</style>
